/* eslint-disable no-console */
import Keycloak from 'keycloak-js';
import { api } from '@/config/axios/index';
import router from '@/router/index';

let store = null;

// minValidity (s) do refresh: renova o token se ele expira dentro desse tempo.
// É um buffer para clock skew + latência da request e NÃO escala com a vida do
// token. Com access token de 24h em homologação e produção não há risco de
// tempestade de refresh; 60s cobre skew/latência com folga.
const TOKEN_MIN_VALIDITY = 60;

var url = process.env.VUE_APP_KEYCLOAK_URL;

let userInfo = {
    name: '',
    email: '',
    authenticated: false,
    userToken: '',
    avatar: null,
    keycloak: null,
};

const keycloak = new Keycloak({
  url: url,
  realm: process.env.VUE_APP_KEYCLOAK_REALM,
  clientId: process.env.VUE_APP_KEYCLOAK_CLIENTID,
});

// Prazo máximo do keycloak.init. Fica ABAIXO do AUTH_READY_TIMEOUT (5s) de
// authReady.js de propósito: assim a reconciliação (reset da sessão morta)
// acontece ANTES de o portão do boot liberar, e a home não chega a mostrar o
// estado logado-falso.
const KEYCLOAK_INIT_TIMEOUT = 4000;

// Corre o keycloak.init contra um timeout. O check-sso depende de um iframe
// silencioso que precisa aterrissar em silent-check-sso.html e chamar
// parent.postMessage; se esse arquivo não for servido como estático (ex.: o
// fallback SPA da CDN devolve index.html no lugar), o init NUNCA resolve e trava
// o boot. Sem este timeout, o app libera pelo setTimeout de authReady mantendo o
// estado logado-falso do localStorage, e a sessão morta só aparece no 1º 401.
// Com ele, a trava vira rejeição e o chamador trata como deslogado.
const initWithTimeout = (options) => Promise.race([
  keycloak.init(options),
  new Promise((_, reject) => {
    setTimeout(() => reject(new Error('keycloak init timeout')), KEYCLOAK_INIT_TIMEOUT);
  }),
]);

export default {
  setStore(s) {
    store = s;
  },

  async initKeycloak(onload) {
    if(onload === 'check-sso') {
      try {
        const auth = await initWithTimeout({
          onLoad: onload,
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          checkLoginIframe: false,
          pkceMethod: 'S256',
        });

        if (auth) {
          await this.updateToken();

          userInfo = {
            name: keycloak.idTokenParsed.name,
            email: keycloak.idTokenParsed.email,
            phone: keycloak.idTokenParsed.phone_number?.replace(
              /(\d{2})?(\d{5})?(\d{4})/,
              '($1) $2-$3'
            ),
            birthdate: keycloak.idTokenParsed.birth_date,
            cpf: keycloak.idTokenParsed.preferred_username,
            avatar: keycloak.idTokenParsed.avatar,
            authenticated: keycloak.authenticated,
            userToken: keycloak.token,
            refreshToken: keycloak.refreshToken,
            keycloak: keycloak,
            userId: keycloak.idTokenParsed.sub,
          };
        } else {
          userInfo.authenticated = keycloak.authenticated;
          userInfo.keycloak = keycloak;
        }
      } catch (error) {
        // Sessão não pôde ser verificada (init travado ou rejeitado — ex.: o
        // check-sso depende do silent-check-sso.html, que a CDN pode devolver
        // como index.html). Trata como deslogado para o store reconciliar via
        // resetUserData, em vez de manter o estado logado-falso do localStorage.
        userInfo.authenticated = false;
        userInfo.keycloak = keycloak;
        console.error(`Ocorreu um erro ao inicializar o sistema
          de autenticação:${error.message || error}`);
      }
    } else {
      try {
        const auth = await initWithTimeout({
          checkLoginIframe: false,
          pkceMethod: 'S256',
        });

        if (auth) {
          userInfo = {
            name: keycloak.idTokenParsed.name,
            email: keycloak.idTokenParsed.email,
            phone: keycloak.idTokenParsed.phone_number?.replace(
              /(\d{2})?(\d{5})?(\d{4})/,
              '($1) $2-$3'
            ),
            birthdate: keycloak.idTokenParsed.birth_date,
            cpf: keycloak.idTokenParsed.preferred_username,
            avatar: keycloak.idTokenParsed.avatar,
            authenticated: keycloak.authenticated,
            userToken: keycloak.token,
            refreshToken: keycloak.refreshToken,
            keycloak: keycloak,
            userId: keycloak.idTokenParsed.sub,
          };
        } else {
          userInfo.authenticated = keycloak.authenticated;
          userInfo.keycloak = keycloak;
        }
      } catch (error) {
        // Sessão não pôde ser verificada (init travado ou rejeitado — ex.: o
        // check-sso depende do silent-check-sso.html, que a CDN pode devolver
        // como index.html). Trata como deslogado para o store reconciliar via
        // resetUserData, em vez de manter o estado logado-falso do localStorage.
        userInfo.authenticated = false;
        userInfo.keycloak = keycloak;
        console.error(`Ocorreu um erro ao inicializar o sistema de autenticação:
        ${error.message || error}`);
      }
    }

    return userInfo;
  },

  async logout() {
    // Logout iniciado pelo usuário. Montamos a URL de end-session ANTES de
    // limpar o estado (ela usa o id token como hint) e navegamos nós mesmos —
    // porque o keycloak.logout() da v26 às vezes NÃO redireciona neste ambiente,
    // deixando a instância inutilizável e os botões de login travados.
    // createLogoutUrl é síncrono na v26 (retorna string). Sem id token (sessão
    // já morta) ou em falha, faz reboot limpo para a home (logout local).
    let logoutUrl = null;

    try {
      if (keycloak.idToken) {
        logoutUrl = keycloak.createLogoutUrl({
          redirectUri: process.env.VUE_APP_KEYCLOAK_REDIRECT,
        });
      }
    } catch (error) {
      logoutUrl = null;
    }

    store.dispatch('resetUserData');
    userInfo.userToken = null;
    userInfo.authenticated = false;

    window.location.replace(logoutUrl || process.env.VUE_APP_KEYCLOAK_REDIRECT);
  },

  async recoverExpiredSession() {
    // Recuperação de SESSÃO MORTA (refresh falhou / 401), diferente do logout
    // iniciado pelo usuário. NÃO chama keycloak.logout() (end-session numa
    // sessão inexistente trava e deixa a instância inconsistente) NEM tenta
    // login in-place (createLoginUrl é async na v26 e relogar numa instância
    // quebrada é frágil). Faz um REBOOT LIMPO: recarrega numa URL neutra (sem
    // fragmento OAuth), recriando uma instância keycloak-js sã — o equivalente
    // automático ao Ctrl+Shift+R. O usuário reloga pelos botões normais (fluxo
    // já testado), evitando o estado travado.
    store.dispatch('resetUserData');
    userInfo.userToken = null;
    userInfo.authenticated = false;

    // Nas rotas de confirmação de ticket, preserva o formulário em andamento:
    // mostra o modal em vez de redirecionar (ver cenário 7).
    const isFinishRoute = ['SolicitationFinish', 'ManifestationFinish']
      .includes(router.currentRoute.name);

    if (isFinishRoute) {
      store.dispatch('setUnauthorizedError', true);

      return;
    }

    // Guard anti-loop: se acabamos de recuperar (reload recente), NÃO recarrega
    // de novo. Sem isso, se o destino re-dispara o 401 (ex.: refresh realmente
    // morto e a página refaz a chamada), cada reload volta a recuperar — loop
    // infinito de reload.
    const RECOVERY_MARK = 'kc_last_recovery';
    const lastRecovery = Number(sessionStorage.getItem(RECOVERY_MARK) || 0);

    if (lastRecovery && Date.now() - lastRecovery < 8000) {
      return;
    }

    sessionStorage.setItem(RECOVERY_MARK, String(Date.now()));

    const fallback = router.currentRoute.matched
      .map((record) => record.meta.anonymousFallback)
      .find(Boolean);

    const target = fallback ?
      router.resolve({ name: fallback }).href :
      process.env.VUE_APP_KEYCLOAK_REDIRECT;

    window.location.replace(target);
  },

  async updateToken() {
    // Durante o boot o Keycloak ainda pode não ter inicializado. Chamadas
    // disparadas antes do init concluir não devem forçar logout (evita a corrida
    // em que uma request sai antes do check-sso e desloga o usuário logado).
    if (!keycloak.authenticated) {
      return;
    }

    if (!keycloak.token || !keycloak.refreshToken) {
      if (store.getters.userIsLogged) {
        await this.recoverExpiredSession();
      }

      return;
    }

    try {
      const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY);

      if (refreshed) {
        store.commit('KEYCLOAK_UPDATE_USER_TOKEN', keycloak.token);
      }
    } catch (error) {
      if (store.getters.userIsLogged) {
        await this.recoverExpiredSession();
      }
      console.error(`Ocorreu um erro ao atualizar o token:
      ${error.message || error}`);
    }
  },

  async autologinKeycloak(token, refreshToken, clientId){
    const autoLoginkeycloak = new Keycloak({
      url: process.env.VUE_APP_KEYCLOAK_URL,
      realm: process.env.VUE_APP_KEYCLOAK_REALM,
      clientId: clientId,
    });

    await autoLoginkeycloak.init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      token: token,
      refreshToken: refreshToken,
    })
    .then((authenticated) => {
      if(authenticated){
        userInfo = {
          name: autoLoginkeycloak.tokenParsed.name,
          email: autoLoginkeycloak.tokenParsed.email,
          phone: autoLoginkeycloak.tokenParsed.phone_number?.replace(/(\d{2})?(\d{5})?(\d{4})/, '($1) $2-$3'),
          birthdate: autoLoginkeycloak.tokenParsed.birth_date,
          cpf: autoLoginkeycloak.tokenParsed.preferred_username,
          avatar: autoLoginkeycloak.tokenParsed.avatar,
          authenticated: autoLoginkeycloak.authenticated,
          userToken: autoLoginkeycloak.token,
          keycloak: autoLoginkeycloak,
          userId: autoLoginkeycloak.tokenParsed.sub,
        };
      }else{
        userInfo.authenticated = autoLoginkeycloak.authenticated;
        userInfo.keycloak = autoLoginkeycloak;
      }
    })
    .catch(function(e) {
      alert(`failed to initialize ${e}`);
    });

    return userInfo;
  },

  getUserInfo(url) {
    return api({
      method: 'GET',
      url: url,
    });
  }
};
